import type { Resource } from "@medplum/fhirtypes";
import type { RefContext } from "../types";

/**
 * What a codec may reach: resolving a domain identity to a reference, and
 * nothing else. Stated as an exclusion rather than an allow-list, so it names
 * the rule instead of recording who happened to call what. `refTo` and
 * `refToMember` take an ENTITY, which a codec working from props cannot supply;
 * `newName` mints a new bundle entry, which is the mapper's job through
 * `member`/`memberSet`, not something encoding one row decides.
 *
 * An allow-list widens by whoever hits the next compile error. Dropping an
 * exclusion here is a decision someone has to defend.
 */
export type EncodeContext = Omit<RefContext, "refTo" | "refToMember" | "newName">;

export type FhirPath = string & { readonly __fhirPath: true };
export const fp = (expression: string): FhirPath => expression as FhirPath;

/** Whole-row translation for exactly the domain fields read from one resource type. */
export interface Codec<P, K extends keyof P, R extends Resource> {
  readonly resourceType: R["resourceType"];
  readonly reads: Readonly<Record<K, readonly FhirPath[]>>;
  readonly writes?: Readonly<Partial<Record<keyof P, readonly FhirPath[]>>>;
  readonly owns?: readonly FhirPath[];
  readonly fieldReaders?: { readonly [Field in K]?: (resource: R) => P[Field] };
  decode(resource: R): Pick<P, K>;
  encode(props: P, prior: R | null, ctx?: EncodeContext): R;
}

/** Type-erased view used by consumers of heterogeneous mapper compositions. */
export interface CompositionCodec {
  readonly fieldReaders?: Readonly<Record<string, (resource: never) => unknown>>;
  decode(resource: never): object;
  encode(props: never, prior: never, ctx?: EncodeContext): Resource;
  readonly resourceType: Resource["resourceType"];
  readonly reads: Readonly<Record<string, readonly FhirPath[]>>;
  readonly owns?: readonly FhirPath[];
}

export interface MapperComposition {
  /** Fields assembled from more than one resource. Declared, never inferred. */
  joins?: readonly string[];
  /**
   * Read into the graph and decoded, never a projection trigger. No `via`: that
   * is the reverse-routing inverse of `fetch`, and a contributor notification is
   * never reverse-routed, so an edge declared here could only ever be decoration.
   */
  contributors?: Record<string, { codec: CompositionCodec; into: string }>;
  anchor: CompositionCodec;
  children: Record<string, ChildComposition>;
}

export interface ChildCodec {
  codec: CompositionCodec;
  into: string;
}

/** One membership set per resource type: kinds share pairing and reverse routing. */
export type ChildComposition = {
  via: string;
  pairBy?: (row: never) => string;
} & (
  | (ChildCodec & { kinds?: never; kindOf?: never })
  | { kinds: Record<string, ChildCodec>; kindOf: (row: never) => string | undefined; codec?: never; into?: never }
);

/** Without a row, enumerate every reader; with a row, select its declared kind. */
export function childCodecs(child: ChildComposition | ChildCodec, row?: Resource): ChildCodec[] {
  if ("kinds" in child && child.kinds) {
    if (!row) return Object.values(child.kinds);
    const kind = child.kindOf(row as never);
    const selected = kind !== undefined && Object.hasOwn(child.kinds, kind) ? child.kinds[kind] : undefined;
    return selected ? [selected] : [];
  }
  return [{ codec: child.codec, into: child.into }];
}

/** A child's concrete codec retains its row, field slice and resource types. */
export interface CompositionChild<Row, K extends keyof Row, R extends Resource, Props> {
  codec: Codec<Row, K, R>;
  into: keyof Props | `${keyof Props & string}.${string}`;
  via: string;
  pairBy?: (row: R) => string;
}

/** Same type retention for a contributor, minus the routing edge it cannot use. */
export interface CompositionContributor<Row, K extends keyof Row, R extends Resource, Props> {
  codec: Codec<Row, K, R>;
  into: keyof Props;
}

type KindResource<Kinds extends Record<string, ChildCodec>> =
  Kinds[keyof Kinds]["codec"] extends Codec<infer _Row, infer _K, infer R> ? R : never;
type TypedKinds<Props, Kinds extends Record<string, ChildCodec>> = {
  [Kind in keyof Kinds]: Kinds[Kind]["codec"] extends Codec<infer Row, infer K, infer R>
    ? Pick<CompositionChild<Row, K, R, Props>, "codec" | "into"> & NoExtraKeys<Kinds[Kind], "codec" | "into">
    : never;
};
type TypedChildren<Props, C extends MapperComposition["children"]> = {
  [Name in keyof C]: C[Name] extends { kinds: infer Kinds extends Record<string, ChildCodec> }
    ? {
        kinds: TypedKinds<Props, Kinds>;
        kindOf: (row: KindResource<Kinds>) => (keyof Kinds & string) | undefined;
        via: string;
        pairBy?: (row: KindResource<Kinds>) => string;
      }
    : C[Name]["codec"] extends Codec<infer Row, infer K, infer R>
      ? CompositionChild<Row, K, R, Props>
      : never;
};

/** Exact, not merely assignable: an extra key here is a routing edge someone expects to work. */
type NoExtraKeys<Member, Allowed extends PropertyKey> = { [Key in Exclude<keyof Member, Allowed>]: never };
type TypedContributors<Props, T extends NonNullable<MapperComposition["contributors"]>> = {
  [Name in keyof T]: T[Name]["codec"] extends Codec<infer Row, infer K, infer R>
    ? CompositionContributor<Row, K, R, Props> & NoExtraKeys<T[Name], "codec" | "into">
    : never;
};

/** Coverage only ever looks at `into`, so children and contributors share these. */
type Members = Record<string, { into: string } | { kinds: Record<string, { into: string }> }>;
type MemberFields<M> = M extends { kinds: infer K extends Members }
  ? ChildFields<K>
  : M extends { into: infer Into extends string }
    ? Into
    : never;
type ChildFields<C extends Members> = MemberFields<C[keyof C]>;
type Head<Path> = Path extends `${infer Key}.${string}` ? Key : Path;
// Joins name aggregate properties, but overlap follows the detector's dotted prefixes.
type Overlap<Left, Right> = Left extends PropertyKey
  ? Right extends PropertyKey
    ? Left extends Right | `${Right & string}.${string}`
      ? Head<Left>
      : Right extends `${Left & string}.${string}`
        ? Head<Left>
        : never
    : never
  : never;
type DuplicateChildFields<C extends Members> = {
  [K in keyof C]:
    | Overlap<MemberFields<C[K]>, MemberFields<C[Exclude<keyof C, K>]>>
    | (C[K] extends { kinds: infer Kinds extends Members } ? DuplicateChildFields<Kinds> : never);
}[keyof C];
type ExactCoverage<
  P,
  A extends CompositionCodec,
  C extends MapperComposition["children"],
  T extends NonNullable<MapperComposition["contributors"]>,
  J extends readonly PropertyKey[],
> = [
  | Exclude<keyof P, keyof A["reads"] | Head<ChildFields<C> | ChildFields<T>>>
  | Exclude<keyof A["reads"] | Head<ChildFields<C> | ChildFields<T>>, keyof P>
  | Exclude<Overlap<keyof A["reads"], ChildFields<C> | ChildFields<T>>, J[number]>
  | Exclude<DuplicateChildFields<C> | DuplicateChildFields<T> | Overlap<ChildFields<C>, ChildFields<T>>, J[number]>
  | Exclude<
      J[number],
      | Overlap<keyof A["reads"], ChildFields<C> | ChildFields<T>>
      | DuplicateChildFields<C>
      | DuplicateChildFields<T>
      | Overlap<ChildFields<C>, ChildFields<T>>
    >,
] extends [never]
  ? unknown
  : never;

/** Every aggregate property has one source unless declared a join; preserve concrete codec types. */
export function defineComposition<Props>() {
  return <
    const A extends CompositionCodec,
    const C extends MapperComposition["children"],
    const T extends NonNullable<MapperComposition["contributors"]> = Record<never, never>,
    const J extends readonly (keyof Props & string)[] = readonly [],
  >(
    composition: {
      anchor: A;
      children: C & TypedChildren<Props, C>;
      contributors?: T & TypedContributors<Props, T>;
      joins?: J;
    } & ExactCoverage<Props, A, C, T, J>,
  ): { anchor: A; children: C; contributors?: NoInfer<T>; joins?: NoInfer<J> } => composition;
}
